import React, { useEffect, useMemo, useState } from "react";
import moment from "moment-timezone";
import Modal from "react-bootstrap/Modal";
import { useFormik } from "formik";
import { Button } from "../../common/Button";
import { failed, success } from "../../common/Toastify";
import { createAvailability, updateAvailability } from "../../api/Scheduling/availability";
import { schedulingErrorMessage } from "../../api/Scheduling/errors";
import { useServiceCategoriesQuery } from "../../../hooks/ReactQueryHooks/useServiceCategoriesQuery";
import { instantRangeToLocal, shiftBreaksToBlockRanges, zonedRangeToInstants } from "./time";
import DateTimeRangeFields from "./DateTimeRangeFields";
import WindowCategoryPicker from "./WindowCategoryPicker";
import ShiftBreaksFields, { createBreakBlocks } from "./ShiftBreaksFields";
import { useBreakOverlapConfirmation } from "./BlockOverlapWarning";

function hoursChanged(existing, values, interval) {
  if (!existing) return true;
  const previous = [...(existing.serviceCategoryIds || [])].sort().join();
  const next = [...(values.serviceCategoryIds || [])].sort().join();
  return (
    !moment(existing.startsAt).isSame(interval.startsAt) ||
    !moment(existing.endsAt).isSame(interval.endsAt) ||
    (existing.mode || "regular") !== (values.mode || "regular") ||
    previous !== next
  );
}

const ManualAvailabilityForm = ({
  show,
  onHide,
  organizationRef,
  schedule,
  window: existing,
  onSaved,
  initialRange,
  timezoneName: displayTimeZone,
}) => {
  const timezoneName = displayTimeZone || schedule?.timezoneName || "UTC";
  const [btnLoading, setBtnLoading] = useState(false);
  const { confirmOverlap, warning } = useBreakOverlapConfirmation();
  const categoryIds = schedule?.serviceCategoryIds || [];
  const isEdit = !!existing?.availabilityId;
  const isRuleGenerated = !!existing?.availabilityRuleId;
  const { data: categories = [] } = useServiceCategoriesQuery({ scope: "descendants", practitionerRef: schedule?.practitionerRef,
    organizationRef,
    active: true,
    enabled: !!organizationRef && show,
  });
  const categoryOptions = useMemo(
    () =>
      categories
        .filter((category) => categoryIds.includes(category.serviceCategoryId))
        .map((category) => ({
          name: `${category.name} (${category.durationMinutes} min)`,
          value: category.serviceCategoryId,
          organizationRef: category.organizationRef, organizationName: category.organizationName || category.organizationRef,
        })),
    [categories, categoryIds]
  );

  const formik = useFormik({
    initialValues: {
      localDate: "",
      endDate: "",
      startTime: "09:00",
      endTime: "17:00",
      mode: "regular",
      serviceCategoryIds: categoryIds,
      breaks: [],
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!values.serviceCategoryIds?.length) {
        failed("Pick at least one service category for this window.");
        return;
      }
      let interval;
      try { interval = zonedRangeToInstants(values, timezoneName); }
      catch (error) { failed(error.message); return; }
      const breakResult = shiftBreaksToBlockRanges(values, values.localDate, timezoneName);
      if (breakResult.error) {
        failed(breakResult.error);
        return;
      }
      const body = {
        organizationRef,
        scheduleId: schedule.scheduleId,
        timeBasis: "instant",
        ...interval,
        mode: values.mode,
        serviceCategoryIds: values.serviceCategoryIds,
      };
      const shouldSaveHours = !isEdit || hoursChanged(existing, values, interval) || !breakResult.ranges.length;
      setBtnLoading(true);
      let hoursSaved = false;
      try {
        if (shouldSaveHours) {
          if (isEdit) {
            await updateAvailability({ ...body, availabilityId: existing.availabilityId });
          } else {
            await createAvailability(body);
          }
          hoursSaved = true;
        }
        await createBreakBlocks({
          organizationRef,
          scheduleId: schedule.scheduleId,
          ranges: breakResult.ranges,
          confirmOverlap,
        });
        success(isEdit ? "Availability updated" : "One-off availability added");
        onSaved?.();
        onHide();
      } catch (error) {
        failed(
          hoursSaved && breakResult.ranges.length
            ? `Hours saved, but a break could not be added. ${schedulingErrorMessage(error)}`
            : schedulingErrorMessage(error)
        );
        if (hoursSaved) {
          onSaved?.();
          onHide();
        }
      } finally {
        setBtnLoading(false);
      }
    },
  });

  useEffect(() => {
    if (!show) return;
    if (existing) {
      formik.setValues({
        ...instantRangeToLocal(existing.startsAt, existing.endsAt, timezoneName),
        mode: existing.mode || "regular",
        serviceCategoryIds: existing.serviceCategoryIds || categoryIds,
        breaks: [],
      });
    } else {
      formik.setValues({
        localDate: initialRange?.localDate || "",
        endDate: initialRange?.endDate || initialRange?.localDate || "",
        startInstant: initialRange?.startInstant,
        endInstant: initialRange?.endInstant,
        startTime: initialRange?.startTime || "09:00",
        endTime: initialRange?.endTime || "17:00",
        mode: "regular",
        serviceCategoryIds: categoryIds,
        breaks: [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, existing, initialRange]);

  return (
    <Modal show={show} onHide={() => { if (!btnLoading) onHide(); }} keyboard={!btnLoading} size="lg" centered backdrop="static" className="custom-dialog scheduling-dialog">
      <Modal.Header closeButton={!btnLoading} className="border-0">
        <Modal.Title>{isEdit ? "Edit working hours" : "Add extra working hours"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isRuleGenerated ? (
          <p className="text-muted">
            Changes apply to this date only.
          </p>
        ) : null}
        <form className="common-form border-fields" onSubmit={formik.handleSubmit}>
          <fieldset disabled={btnLoading}>
          <DateTimeRangeFields values={formik.values} onChange={formik.setFieldValue} timezoneName={timezoneName} />
          <WindowCategoryPicker
            options={categoryOptions}
            value={formik.values.serviceCategoryIds || []}
            onChange={(next) => formik.setFieldValue("serviceCategoryIds", next)}
          />
          <ShiftBreaksFields
            shift={formik.values} localDate={formik.values.localDate} timezoneName={timezoneName}
            breaks={formik.values.breaks || []}
            onChange={(breaks) => formik.setFieldValue("breaks", breaks)}
          />
          </fieldset>
          {warning}
          {!warning && <div className="btn-wrap" style={{ display: "flex", marginTop: "1rem" }}>
            <Button type="button" variant="secondary" disabled={btnLoading} onClick={onHide}>
              Cancel
            </Button>
            <Button type="submit" isLoading={btnLoading}>
              Save
            </Button>
          </div>}
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default ManualAvailabilityForm;
